// 官网实时推送客户端（webman/push · pusher 协议子集）
//
// 设计同扩展端：只收轻量 envelope（event/reason/occurred_at），收到即拉权威状态，
// 规避乱序/丢包一致性问题（与 /site/user/state 快照架构同构）。
//
// 生命周期：登录后由 App.vue 启动、登出断开。收银台仍以轮询为准（跳转收银时 WS 会中断，
// 轮询更稳），本客户端作为辅助即时刷新（entitlement.changed 静默刷新 + 广播事件供页面自刷；
// session.revoked 清态跳登录页）。

import { sign, generateNonce, loadSecret } from '@/api/sign'
import { serverTime } from '@/api/serverTime'
import { API_BASE_URL, SITE_APPKEY } from '@/config'
import { useAuthStore } from '@/stores/auth'
import { apiUserState } from '@/api/auth'
import { routerHolder } from '@/router/instance'

const PUSH_APP_KEY = (import.meta.env.VITE_PUSH_APP_KEY as string | undefined) || 'yfb-push'
// 显式 WS 基址（可选）：dev 环境 .env.development 指向 ws://127.0.0.1:3131 直连本地推送；
// grey/prod 留空走同源推导（nginx 按域反代到本环境 webman/push 端口，见 api docs/server/push.md §2.5）。
const PUSH_WS_URL_ENV = import.meta.env.VITE_PUSH_WS_URL as string | undefined

let ws: WebSocket | null = null
let socketId = ''
let stopped = true
let reconnectTimer: ReturnType<typeof setTimeout> | null = null
let pingTimer: ReturnType<typeof setInterval> | null = null
let reconnectDelay = 1000
const MAX_DELAY = 60000
// 连续握手失败长冷却：push 只是加速器，断连由轮询/TTL 兜底（docs/server/push.md §6 降级矩阵），
// 服务端 push 进程未启动期间（本环境 WS 端口无人监听 → nginx 502）无限重连只会造成每分钟一次的
// 握手报错刷屏。连续 FAIL_STREAK_COOLDOWN 次未连上即转入 LONG_COOLDOWN_MS 慢自愈；onopen 归零。
const FAIL_STREAK_COOLDOWN = 6
const LONG_COOLDOWN_MS = 5 * 60 * 1000
let failStreak = 0

function wsUrl(): string {
  if (PUSH_WS_URL_ENV) return `${PUSH_WS_URL_ENV}/app/${PUSH_APP_KEY}`
  const host = typeof location !== 'undefined' ? location.host : 'woofootball.site'
  // 与后端同域部署，经 nginx 把 /push 反代到本环境 webman/push 的 ws 端口
  return `wss://${host}/push/app/${PUSH_APP_KEY}`
}

function nowTs(): string {
  return String(serverTime.getTimestampSeconds())
}

/** 向 /site/push/auth 取私有频道订阅签名（复用 site 签名链路 + JWT） */
async function fetchSubscribeAuth(channel: string, sid: string): Promise<string | null> {
  const auth = useAuthStore()
  if (!auth.isLoggedIn || !auth.token) return null

  // 40122 签名超时自愈：fetch 无拦截器，手动实现一次重试（换新 nonce）
  let retried = false
  for (;;) {
    const ts = nowTs()
    const nonce = generateNonce()
    const params: Record<string, unknown> = { appkey: SITE_APPKEY, timestamp: ts, nonce }
    if (auth.tokenHash) params.token_hash = auth.tokenHash
    const sig = await sign(params, loadSecret())
    const body: Record<string, unknown> = {
      channel_name: channel,
      socket_id: sid
    }
    // 签名参数放请求头（与 siteClient 拦截器一致）：后端 site Sign 中间件从请求头做 nonce 检查 + 验签，
    // 本客户端早期误把这些参数放 body，导致 /site/push/auth 恒 40121「缺少必要参数」。
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${auth.token}`,
      timestamp: ts,
      nonce,
      appkey: SITE_APPKEY,
      sign: sig
    }
    if (auth.tokenHash) headers.token_hash = auth.tokenHash
    try {
      const resp = await fetch(`${API_BASE_URL}/site/push/auth`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      })
      if (resp.status === 401 && !retried) {
        serverTime.forceSyncFromResponse(resp.headers.get('date'))
        retried = true
        continue
      }
      const dateHeader = resp.headers.get('date')
      serverTime.syncFromResponse(dateHeader)
      const j = (await resp.json()) as { auth?: string; data?: { auth?: string } }
      return j?.auth || j?.data?.auth || null
    } catch {
      return null
    }
  }
}

function send(obj: unknown): void {
  if (ws && ws.readyState === WebSocket.OPEN) {
    try {
      ws.send(JSON.stringify(obj))
    } catch {
      // ignore
    }
  }
}

/** 连接建立后取 socket_id → 订阅本人私有频道 */
async function doSubscribe(): Promise<void> {
  if (!socketId) return
  const auth = useAuthStore()
  if (!auth.isLoggedIn) return
  // 复用 useAuthSync 已写入的 userInfo.id，避免为取 uid 再发一次 /site/user/state
  const uid = auth.userInfo?.id ?? 0
  if (!uid) return
  const channel = `private-user-${uid}`
  const authSig = await fetchSubscribeAuth(channel, socketId)
  if (authSig) {
    send({ event: 'pusher:subscribe', data: { channel, auth: authSig } })
  }
}

/** entitlement.changed：拉最新状态 + 广播事件供页面自刷新 */
async function refreshEntitlement(): Promise<void> {
  const auth = useAuthStore()
  if (!auth.isLoggedIn) return
  try {
    const state = await apiUserState()
    auth.setUserInfo(state.user)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('yfb:entitlement-changed'))
    }
  } catch {
    // 401 由拦截器处理
  }
}

/** session.revoked：清本地登录态 + 跳登录页（reason=kicked） */
function handleRevoked(): void {
  const auth = useAuthStore()
  auth.clear()
  const router = routerHolder.instance
  if (!router) return
  const current = router.currentRoute.value
  if (current.name === 'login') return
  const redirect = current.fullPath
  const query: Record<string, string> = { reason: 'kicked' }
  if (redirect && redirect !== '/') query.redirect = redirect
  void router.replace({ path: '/login', query })
}

function onMessage(ev: MessageEvent): void {
  let msg: { event?: string; data?: unknown }
  try {
    msg = JSON.parse(ev.data as string)
  } catch {
    return
  }
  if (!msg || !msg.event) return

  if (msg.event === 'pusher:connection_established') {
    try {
      const data = typeof msg.data === 'string' ? JSON.parse(msg.data) : (msg.data as { socket_id?: string })
      socketId = data?.socket_id || ''
    } catch {
      socketId = ''
    }
    if (socketId) void doSubscribe()
    return
  }
  if (msg.event === 'pusher:ping') {
    send({ event: 'pusher:pong', data: {} })
    return
  }
  if (msg.event === 'pusher:pong') return
  if (msg.event === 'entitlement.changed') {
    void refreshEntitlement()
    return
  }
  if (msg.event === 'order.paid') {
    // 收银台据此即时结算（替代 2.5s 轮询）；order_no 由客户端匹配具体订单
    const data = (msg.data ?? {}) as { order_no?: string }
    if (typeof window !== 'undefined' && data.order_no) {
      window.dispatchEvent(new CustomEvent('yfb:order-paid', { detail: { order_no: data.order_no } }))
    }
    return
  }
  if (msg.event === 'session.revoked') {
    handleRevoked()
    stopPushClient()
    return
  }
}

function scheduleReconnect(): void {
  if (stopped) return
  if (reconnectTimer) clearTimeout(reconnectTimer)
  // 后台标签暂停重连调度（与 PayCashier 轮询的 visibility 门控同思路）：
  // 不设 timer，reconnectTimer 保持为空，回前台由 onVisibilityChange 立即补连
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
  const base = failStreak >= FAIL_STREAK_COOLDOWN ? LONG_COOLDOWN_MS : reconnectDelay
  const delay = base + Math.floor(Math.random() * 1000)
  reconnectTimer = setTimeout(() => {
    reconnectDelay = Math.min(reconnectDelay * 2, MAX_DELAY)
    connect()
  }, delay)
}

/** 回前台补连：仅当未连接且没有挂起的重连 timer（后台被抑制调度）时立即连接 */
function onVisibilityChange(): void {
  if (typeof document === 'undefined' || document.visibilityState !== 'visible') return
  if (stopped || ws || reconnectTimer) return
  connect()
}

function connect(): void {
  if (stopped) return
  if (typeof WebSocket === 'undefined') return
  try {
    ws = new WebSocket(wsUrl())
  } catch {
    scheduleReconnect()
    return
  }
  ws.onopen = () => {
    reconnectDelay = 1000
    failStreak = 0 // 连续失败计数归零（长冷却解除）
  }
  ws.onmessage = onMessage
  ws.onclose = () => {
    ws = null
    failStreak++
    scheduleReconnect()
  }
  ws.onerror = () => {
    try {
      ws?.close()
    } catch {
      // ignore
    }
  }
}

export function startPushClient(): void {
  if (typeof window === 'undefined') return
  if (!stopped) return
  stopped = false
  failStreak = 0 // 新会话从指数退避重新开始
  if (pingTimer) clearInterval(pingTimer)
  pingTimer = setInterval(() => {
    send({ event: 'pusher:ping', data: {} })
  }, 45000)
  document.addEventListener('visibilitychange', onVisibilityChange)
  connect()
}

export function stopPushClient(): void {
  stopped = true
  failStreak = 0
  document.removeEventListener('visibilitychange', onVisibilityChange)
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  if (pingTimer) {
    clearInterval(pingTimer)
    pingTimer = null
  }
  if (ws) {
    try {
      ws.onclose = null
      ws.close()
    } catch {
      // ignore
    }
    ws = null
  }
}
